import { firstValueFrom, Observable } from 'rxjs';

import { ItemDeletedResponse, MetadataAttachment } from '@jms/model';

/** A file the server did not delete, with the reason it gave. */
export interface FileDeleteFailure {
  attachment: MetadataAttachment;
  reason: string;
}

/**
 * Deletes every file and resolves with the ones that failed, for the Files dashboard's single and bulk delete.
 * Each delete settles on its own, so one refusal (a read-only integration file, a network error) neither stops
 * the others nor escapes as an uncaught error.
 */
export async function deleteFiles(attachments: MetadataAttachment[], deleteOne: (attachment: MetadataAttachment) => Observable<ItemDeletedResponse>): Promise<FileDeleteFailure[]> {

  const results = await Promise.all(attachments.map(attachment => firstValueFrom(deleteOne(attachment))
    .then(
      () => null,
      (error: Error) => ({ attachment, reason: error?.message || 'The file could not be deleted.' }),
    )));

  return results.filter(result => !!result);
}

/** "1 file" / "3 files", for the Files dashboard's delete messages. */
export function countFiles(count: number): string {

  return `${count} ${count === 1 ? 'file' : 'files'}`;
}

/** The bulk delete confirmation: how many files will go, and how many read-only integration files will be left. */
export function deleteFilesQuestion(deletable: number, readOnly: number): string {

  const question = deletable === 1
    ? 'Are you sure you want to delete the selected file?'
    : `Are you sure you want to delete the ${deletable} selected files?`;

  if (!readOnly) return question;

  return `${question} ${countFiles(readOnly)} came from an integration and will be left, because ${readOnly === 1 ? 'it is' : 'they are'} read-only.`;
}
